/**
 * CSV Import/Export Controls for Budget app
 */

import React, { useRef, useState } from 'react';
import { useBudget } from '../contexts/BudgetContext';
import { BudgetEntry } from '../types/budget.types';
import {
  exportEntriesToCSV,
  exportBudgetConfigToCSV,
  parseEntriesFromCSV,
  convertParsedEntriesToBudgetEntries,
  downloadCSV,
  readFileAsText,
  validateCSVStructure,
  CSVValidationResult,
  ParsedCSVEntry,
} from '../utils/csvUtils';

interface CsvControlsProps {
  className?: string;
}

export const CsvControls: React.FC<CsvControlsProps> = ({ className }) => {
  const { currentBudget, entries, createEntry } = useBudget();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
    details?: CSVValidationResult;
  } | null>(null);
  const [previewData, setPreviewData] = useState<ParsedCSVEntry[]>([]);
  const [pendingContent, setPendingContent] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleExportEntries = () => {
    if (!currentBudget) return;

    const budgetEntries = entries.filter(e => e.budgetId === currentBudget.id);
    const csv = exportEntriesToCSV(
      budgetEntries,
      currentBudget.subjects,
      currentBudget.sections
    );

    const filename = `${currentBudget.name.replace(/\s+/g, '_')}_entries_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, filename);
  };

  const handleExportConfig = () => {
    if (!currentBudget) return;

    const csv = exportBudgetConfigToCSV(currentBudget);
    const filename = `${currentBudget.name.replace(/\s+/g, '_')}_config_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, filename);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentBudget) return;

    setImportResult(null);

    try {
      const content = await readFileAsText(file);
      const validation = validateCSVStructure(content);

      if (!validation.isValid) {
        setImportResult({
          success: false,
          message: 'Invalid CSV file structure',
          details: validation,
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      const parsedEntries = parseEntriesFromCSV(content);
      // Show preview of first 10 rows before committing
      setPreviewData(parsedEntries.slice(0, 10));
      setPendingContent(content);
      setShowPreview(true);
    } catch (err) {
      setImportResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to read CSV',
      });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingContent || !currentBudget) return;

    setShowPreview(false);
    setImporting(true);

    try {
      const parsedEntries = parseEntriesFromCSV(pendingContent);
      const budgetEntries = convertParsedEntriesToBudgetEntries(
        parsedEntries,
        currentBudget.id,
        currentBudget.subjects,
        currentBudget.sections
      );

      let successCount = 0;
      for (const entry of budgetEntries) {
        try {
          await createEntry(entry as Omit<BudgetEntry, 'id' | 'createdAt' | 'updatedAt'>);
          successCount++;
        } catch (err) {
          console.error('Error adding entry:', err);
        }
      }

      setImportResult({
        success: true,
        message: `Successfully imported ${successCount} of ${parsedEntries.length} entries`,
      });
    } catch (err) {
      setImportResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to import CSV',
      });
    } finally {
      setImporting(false);
      setPendingContent(null);
      setPreviewData([]);
    }
  };

  const handleCancelImport = () => {
    setShowPreview(false);
    setPendingContent(null);
    setPreviewData([]);
  };

  if (!currentBudget) {
    return null;
  }

  return (
    <div className={`csv-controls ${className || ''}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv"
        style={{ display: 'none' }}
      />

      <div className="csv-controls-buttons">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleExportEntries}
          title="Export budget entries to CSV"
        >
          Export Entries
        </button>

        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleExportConfig}
          title="Export budget configuration to CSV"
        >
          Export Config
        </button>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleImportClick}
          disabled={importing}
          title="Import entries from CSV"
        >
          {importing ? 'Importing...' : 'Import CSV'}
        </button>
      </div>

      {importResult && (
        <div className={`csv-import-result ${importResult.success ? 'success' : 'error'}`}>
          <p>{importResult.message}</p>
          {importResult.details?.warnings && importResult.details.warnings.length > 0 && (
            <ul className="warnings">
              {importResult.details.warnings.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          )}
          {importResult.details?.errors && importResult.details.errors.length > 0 && (
            <ul className="errors">
              {importResult.details.errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showPreview && (
        <div className="csv-preview-overlay">
          <div className="csv-preview-modal">
            <div className="csv-preview-header">
              <h3>Data Preview</h3>
              <span className="csv-preview-count">Showing first {previewData.length} rows</span>
            </div>
            <div className="csv-preview-table-wrap">
              <table className="csv-preview-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Subject</th>
                    <th>Type</th>
                    <th>Section</th>
                    <th>Measure</th>
                    <th>Amount</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, i) => (
                    <tr key={i}>
                      <td>{row.period}</td>
                      <td>{row.subjectName}</td>
                      <td>{row.subjectType}</td>
                      <td>{row.sectionName}</td>
                      <td>{row.measure}</td>
                      <td>{row.amount}</td>
                      <td>{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="csv-preview-actions">
              <button type="button" className="btn btn-secondary" onClick={handleCancelImport}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleConfirmImport}>
                Confirm Import
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .csv-controls {
          padding: 1rem;
          background: var(--color-surface, #fff);
          border-radius: var(--radius, 8px);
          border: 1px solid var(--color-border, #e0e0e0);
        }

        .csv-controls-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .csv-import-result {
          margin-top: 1rem;
          padding: 0.75rem;
          border-radius: var(--radius, 8px);
          font-size: 0.875rem;
        }

        .csv-import-result.success {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .csv-import-result.error {
          background: #ffebee;
          color: #c62828;
        }

        .csv-import-result ul {
          margin: 0.5rem 0 0;
          padding-left: 1.5rem;
          font-size: 0.75rem;
        }

        .csv-import-result .warnings {
          color: #f57c00;
        }

        .csv-import-result .errors {
          color: #c62828;
        }

        .csv-preview-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .csv-preview-modal {
          background: #fff;
          border-radius: 8px;
          padding: 1.5rem;
          max-width: 800px;
          width: 90vw;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .csv-preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .csv-preview-header h3 {
          margin: 0;
          font-size: 1rem;
        }

        .csv-preview-count {
          font-size: 0.8rem;
          color: #666;
        }

        .csv-preview-table-wrap {
          overflow: auto;
          flex: 1;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
        }

        .csv-preview-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
        }

        .csv-preview-table th {
          background: #f5f5f5;
          padding: 0.5rem 0.75rem;
          text-align: left;
          border-bottom: 1px solid #e0e0e0;
          white-space: nowrap;
        }

        .csv-preview-table td {
          padding: 0.4rem 0.75rem;
          border-bottom: 1px solid #f0f0f0;
        }

        .csv-preview-table tr:last-child td {
          border-bottom: none;
        }

        .csv-preview-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }
      `}</style>
    </div>
  );
};

export default CsvControls;
